import { Capacitor } from '@capacitor/core';
import { extractNativeTranscript } from './nativeTranscript';
import { AI_API_URL } from '../constants';

// 🚀 웹 전용 메트릭 시스템
class WebTranscriptMetrics {
  constructor() {
    this.storageKey = 'tubedigest_web_transcript_metrics';
  }

  getMetrics() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {
        totalAttempts: 0,
        successCount: 0,
        errorTypes: {},
        lastUpdated: Date.now(),
        successRate: 0
      };
    } catch {
      return { totalAttempts: 0, successCount: 0, errorTypes: {}, lastUpdated: Date.now(), successRate: 0 };
    }
  }

  recordAttempt(success, errorType = null) {
    const metrics = this.getMetrics();
    metrics.totalAttempts += 1;
    
    if (success) {
      metrics.successCount += 1;
    } else if (errorType) {
      metrics.errorTypes[errorType] = (metrics.errorTypes[errorType] || 0) + 1;
    }
    
    metrics.successRate = metrics.totalAttempts > 0 ? (metrics.successCount / metrics.totalAttempts * 100).toFixed(1) : 0;
    metrics.lastUpdated = Date.now();
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(metrics));
    } catch {
      // localStorage 실패 시 무시
    }
    
    console.log(`📊 [웹 자막 메트릭] 성공률: ${metrics.successRate}% (${metrics.successCount}/${metrics.totalAttempts})`);
  }

  getSuccessRate() {
    return this.getMetrics().successRate;
  }
}

const webMetrics = new WebTranscriptMetrics();

// 에러 타입 상수 정의 (웹 전용 에러 추가)
export const ERROR_TYPES = {
  NO_CAPTIONS: 'NO_CAPTIONS',
  INVALID_URL: 'INVALID_URL',
  SERVER_ERROR: 'SERVER_ERROR',
  SERVER_TIMEOUT: 'SERVER_TIMEOUT', // 새 추가
  SERVER_OVERLOAD: 'SERVER_OVERLOAD', // 새 추가
  NETWORK_ERROR: 'NETWORK_ERROR',
  CORS_ERROR: 'CORS_ERROR', // 새 추가
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

export const ERROR_MESSAGES = {
  [ERROR_TYPES.NO_CAPTIONS]: "이 영상에는 자막이 없습니다.",
  [ERROR_TYPES.INVALID_URL]: "올바른 YouTube URL이 아닙니다.",
  [ERROR_TYPES.SERVER_ERROR]: "서버에 문제가 발생했습니다.",
  [ERROR_TYPES.SERVER_TIMEOUT]: "서버 응답이 너무 오래 걸립니다. 잠시 후 다시 시도해주세요.",
  [ERROR_TYPES.SERVER_OVERLOAD]: "서버가 과부하 상태입니다. 잠시 후 다시 시도해주세요.",
  [ERROR_TYPES.NETWORK_ERROR]: "네트워크 연결을 확인해주세요.",
  [ERROR_TYPES.CORS_ERROR]: "브라우저 보안 정책으로 인한 오류입니다. 페이지를 새로고침해주세요.",
  [ERROR_TYPES.UNKNOWN_ERROR]: "알 수 없는 오류가 발생했습니다."
};

export class YouTubeError extends Error {
  constructor(type, originalError = null) {
    super(ERROR_MESSAGES[type] || ERROR_MESSAGES.UNKNOWN_ERROR);
    this.name = 'YouTubeError';
    this.type = type;
    this.originalError = originalError;
  }
}

function validateYouTubeUrl(url) {
  // 🔧 더 유연한 YouTube URL 패턴 (추가 파라미터 허용)
  const patterns = [
    // 표준 YouTube URL (추가 파라미터 허용)
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    // 모바일 YouTube URL
    /(?:https?:\/\/)?(?:m\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    // 직접 비디오 ID (11자리)
    /^([a-zA-Z0-9_-]{11})$/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      console.log('✅ YouTube URL 검증 성공', { 
        originalUrl: url, 
        videoId: match[1], 
        pattern: pattern.toString() 
      });
      return match[1]; // 비디오 ID 반환
    }
  }

  console.error('❌ YouTube URL 검증 실패', { 
    url, 
    note: '지원되는 형식: youtube.com/watch?v=ID, youtu.be/ID, m.youtube.com/watch?v=ID' 
  });
  throw new YouTubeError(ERROR_TYPES.INVALID_URL);
}

async function getTranscriptFromServer(videoUrl, platform = 'web', userId = null, historyId = null) {
  try {
    const videoId = validateYouTubeUrl(videoUrl);
    console.log('서버에서 자막 가져오기 시도', { videoId, platform, userId: !!userId, historyId: !!historyId });

    // 🚀 플랫폼별 타임아웃 설정
    const timeoutDuration = platform === 'web' ? 25000 : 15000; // 웹: 25초, 네이티브: 15초
    console.log(`⏰ [서버 자막] ${platform} 플랫폼 타임아웃: ${timeoutDuration/1000}초`);

    // 타임아웃 프로미스 생성
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new YouTubeError(ERROR_TYPES.SERVER_TIMEOUT, `서버 자막 추출 타임아웃 (${timeoutDuration/1000}초 초과)`));
      }, timeoutDuration);
    });

    // 서버 요청 프로미스 - userId와 historyId 포함
    const requestBody = { url: videoUrl };
    if (userId) requestBody.user_id = userId;
    if (historyId) requestBody.history_id = historyId;
    
    const requestPromise = fetch(`${AI_API_URL}/api/summarize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    // 타임아웃과 요청을 경합
    const response = await Promise.race([requestPromise, timeoutPromise]);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('서버 응답 에러', { error: errorText, videoId, platform, status: response.status });
      
      // 🚀 웹 전용: HTTP 상태 코드별 세분화된 에러 처리
      if (response.status === 503 || response.status === 502) {
        throw new YouTubeError(ERROR_TYPES.SERVER_OVERLOAD, `서버 과부하 (${response.status})`);
      } else if (response.status === 504) {
        throw new YouTubeError(ERROR_TYPES.SERVER_TIMEOUT, `서버 게이트웨이 타임아웃 (${response.status})`);
      } else if (response.status >= 500) {
        throw new YouTubeError(ERROR_TYPES.SERVER_ERROR, `서버 내부 오류 (${response.status})`);
      } else {
        throw new YouTubeError(ERROR_TYPES.SERVER_ERROR, `서버 응답 오류 (${response.status}): ${errorText}`);
      }
    }

    const data = await response.json();
    console.log('서버에서 자막 가져오기 성공', { videoId, platform, sourceLanguage: data.source_language });
    return {
      transcript: data.transcript,
      sourceLanguage: data.source_language
    };
  } catch (error) {
    if (error instanceof YouTubeError) throw error;
    
    // 🚀 웹 전용: 네트워크 에러 세분화
    if (!navigator.onLine) {
      console.error('네트워크 연결 없음', { videoId, platform });
      throw new YouTubeError(ERROR_TYPES.NETWORK_ERROR, '인터넷 연결이 끊어졌습니다');
    }
    
    // CORS 에러 감지 (웹에서만 발생)
    if (platform === 'web' && (error.name === 'TypeError' && error.message.includes('fetch'))) {
      console.error('CORS 에러 감지', { error: error.message, videoId, platform });
      throw new YouTubeError(ERROR_TYPES.CORS_ERROR, error.message);
    }
    
    // 네트워크 타임아웃 에러
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      console.error('네트워크 타임아웃', { error: error.message, videoId, platform });
      throw new YouTubeError(ERROR_TYPES.SERVER_TIMEOUT, error.message);
    }
    
    console.error('서버 에러', { error, videoId, platform });
    throw new YouTubeError(ERROR_TYPES.SERVER_ERROR, error.message || '알 수 없는 서버 오류');
  }
}

/**
 * 플랫폼별 자막 추출 전략
 * - 네이티브 앱: 네이티브 → 서버 폴백
 * - 웹 앱: 서버 방식만 사용 (메트릭 포함)
 */
export async function getTranscript(videoUrl, userId = null, historyId = null) {
  const isWebPlatform = !Capacitor.isNativePlatform();
  
  try {
    // 네트워크 연결 확인
    if (!navigator.onLine) {
      console.error('네트워크 연결 없음');
      if (isWebPlatform) webMetrics.recordAttempt(false, ERROR_TYPES.NETWORK_ERROR);
      throw new YouTubeError(ERROR_TYPES.NETWORK_ERROR);
    }

    const videoId = validateYouTubeUrl(videoUrl);
    console.log('자막 추출 시작', { videoId, userId: !!userId, historyId: !!historyId });
    
    // 📱 네이티브 플랫폼: 네이티브 → 서버 폴백
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      console.log(`📱 네이티브 플랫폼 감지 (${platform}) - 네이티브 자막 추출 시도`);
      
      try {
        // 🔧 이미 검증된 videoId를 전달하여 중복 검증 방지
        const nativeResult = await extractNativeTranscript(videoUrl, null, true, videoId);
        
        if (nativeResult && nativeResult.transcript) {
          console.log(`✅ 네이티브 자막 추출 성공 (${platform})`, { 
            videoId, 
            transcriptLength: nativeResult.transcript.length,
            source: nativeResult.source,
            method: nativeResult.method,
            sourceLanguage: nativeResult.sourceLanguage
          });
          
          return {
            transcript: nativeResult.transcript,
            source: 'native',
            platform: platform,
            method: nativeResult.method || 'NATIVE_CAPACITOR_HTTP',
            sourceLanguage: nativeResult.sourceLanguage
          };
        }
      } catch (nativeError) {
        console.warn(`❌ 네이티브 자막 추출 실패 (${platform}), 서버 방식으로 폴백`, { 
          error: nativeError.message, 
          errorType: nativeError.name,
          videoId 
        });
        
        // 네이티브 실패 시 서버 폴백
        const serverResult = await getTranscriptFromServer(videoUrl, 'web', userId, historyId); // 네이티브 실패 시 웹 플랫폼으로 폴백
        return {
          transcript: serverResult.transcript,
          source: 'server',
          platform: platform,
          method: 'SERVER_FALLBACK',
          sourceLanguage: serverResult.sourceLanguage
        };
      }
    }
    
    // 🌐 웹 플랫폼: 서버 방식만 사용 (메트릭 포함)
    console.log('🌐 웹 플랫폼 - 서버 방식으로 자막 추출');
    console.log(`📊 [웹 메트릭] 현재 성공률: ${webMetrics.getSuccessRate()}%`);
    
    try {
      const serverResult = await getTranscriptFromServer(videoUrl, 'web', userId, historyId);
      
      // 🚀 웹 성공 메트릭 기록
      webMetrics.recordAttempt(true);
      
      return {
        transcript: serverResult.transcript,
        source: 'server',
        platform: 'web',
        method: 'SERVER_ONLY',
        sourceLanguage: serverResult.sourceLanguage
      };
    } catch (serverError) {
      // 🚀 웹 실패 메트릭 기록
      const errorType = serverError.type || ERROR_TYPES.UNKNOWN_ERROR;
      webMetrics.recordAttempt(false, errorType);
      throw serverError;
    }
    
  } catch (error) {
    if (error instanceof YouTubeError) {
      // 이미 처리된 에러는 메트릭만 확인하고 다시 던지기
      if (isWebPlatform && !error._metricsRecorded) {
        webMetrics.recordAttempt(false, error.type);
        error._metricsRecorded = true; // 중복 기록 방지
      }
      throw error;
    }
    console.error('예상치 못한 에러', { error, videoUrl });
    if (isWebPlatform) webMetrics.recordAttempt(false, ERROR_TYPES.UNKNOWN_ERROR);
    throw new YouTubeError(ERROR_TYPES.UNKNOWN_ERROR, error);
  }
} 